import React, { useState, useEffect } from 'react';
import { Button, Form, Input, Alert, message, Spin } from 'antd';
import axios from 'axios';

const backend_url = process.env.REACT_APP_BACKEND_URL;

const BullhornIntegration = ({ getCookie, fetchSettings }) => {
    const [form] = Form.useForm();
    const [isIntegrated, setIsIntegrated] = useState(false);
    const [bullhornInfo, setBullhornInfo] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [showConfirmRemove, setShowConfirmRemove] = useState(false);
    const [isProcessingRedirect, setIsProcessingRedirect] = useState(false);

    useEffect(() => {
        // Only check for Bullhorn status on initial load
        // Redirect handling is now done at the SettingsPage level
        checkBullhornStatus();
    }, []);

    const checkBullhornRedirect = () => {
        // This function is kept for backward compatibility
        // But the main redirect processing now happens in SettingsPage
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');
        const state = url.searchParams.get('state');

        // Only process if it looks like a Bullhorn redirect and wasn't already handled
        if (code && state && state.startsWith('bullhorn-')) {
            console.log("Detected Bullhorn redirect with code and state in BullhornIntegrationPanel");
            setIsProcessingRedirect(true);
            completeBullhornAuth(code, state);

            // Clean up URL for better user experience
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    };

    const checkBullhornStatus = async () => {
        try {
            const response = await axios.get(
                `${backend_url}/v1/bullhorn-status?sessionID=${getCookie("userSession")}`
            );

            if (response.data.integrated) {
                setIsIntegrated(true);
                setBullhornInfo(response.data.credentials);
            }
        } catch (error) {
            console.error("Error checking Bullhorn status:", error);
        }
    };

    const startBullhornAuth = async (values) => {
        setIsSubmitting(true);
        try {
            console.log("Starting Bullhorn auth with credentials");
            const response = await axios.post(
                `${backend_url}/v1/bullhorn-auth`,
                {
                    sessionID: getCookie("userSession"),
                    apiUsername: values.apiUsername,
                    apiPassword: values.apiPassword,
                    clientID: values.clientID,
                    clientSecret: values.clientSecret
                }
            );

            if (response.data.authUrl) {
                console.log("Received auth URL, redirecting to Bullhorn");
                window.location.href = response.data.authUrl;
            } else {
                message.error("Invalid response from server");
                setIsSubmitting(false);
            }
        } catch (error) {
            message.error("Failed to start Bullhorn authentication");
            console.error("Error starting Bullhorn auth:", error);
            setIsSubmitting(false);
        }
    };

    const completeBullhornAuth = async (code, state) => {
        try {
            console.log("Completing Bullhorn auth with code:", code, "state:", state);

            // Add more detailed logging for debugging
            const requestData = {
                sessionID: getCookie("userSession"),
                code,
                state
            };
            console.log("Request data for complete auth:", requestData);

            const response = await axios.post(
                `${backend_url}/v1/bullhorn-complete`,
                requestData
            );

            if (response.data.success) {
                message.success("Bullhorn integration completed successfully");
                setIsIntegrated(true);
                setBullhornInfo(response.data.credentials);
                if (fetchSettings) fetchSettings();
            } else {
                message.error("Failed to complete Bullhorn integration");
            }
        } catch (error) {
            console.error("Error completing Bullhorn integration:", error);

            // More detailed error message
            let errorMessage = "Failed to complete Bullhorn integration";
            if (error.response) {
                errorMessage += `: ${error.response.status} - ${JSON.stringify(error.response.data)}`;
            } else if (error.request) {
                errorMessage += ": No response received from server";
            } else {
                errorMessage += `: ${error.message}`;
            }

            message.error(errorMessage);
        } finally {
            setIsProcessingRedirect(false);
        }
    };

    const removeBullhorn = async () => {
        setIsRemoving(true);
        try {
            await axios.delete(
                `${backend_url}/v1/bullhorn-integrate?sessionID=${getCookie("userSession")}`
            );

            message.success("Bullhorn integration removed successfully");
            setIsIntegrated(false);
            setBullhornInfo(null);
            if (fetchSettings) fetchSettings();
        } catch (error) {
            message.error("Failed to remove Bullhorn integration");
            console.error("Error removing Bullhorn integration:", error);
        } finally {
            setIsRemoving(false);
            setShowConfirmRemove(false);
        }
    };

    if (isProcessingRedirect) {
        return (
            <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
                <p style={{ marginTop: '10px', color: '#ffffff' }}>Completing Bullhorn integration...</p>
            </div>
        );
    }

    if (isIntegrated) {
        return (
            <div>
                <Alert
                    message="Bullhorn integration is active"
                    type="success"
                    showIcon
                    style={{ marginBottom: 20 }}
                />

                {bullhornInfo && (
                    <div style={{ marginBottom: 20, color: '#ffffff' }}>
                        <p><strong>Bullhorn Corporation ID:</strong> {bullhornInfo.BullhornCorpID}</p>
                        <p><strong>API Username:</strong> {bullhornInfo.APIUsername}</p>
                    </div>
                )}

                {showConfirmRemove ? (
                    <div>
                        <p style={{ color: '#ffffff' }}>Are you sure you want to remove the Bullhorn integration?</p>
                        <Button
                            danger
                            onClick={removeBullhorn}
                            loading={isRemoving}
                            style={{
                                marginRight: 10,
                                backgroundColor: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px'
                            }}
                        >
                            Yes, remove
                        </Button>
                        <Button
                            onClick={() => setShowConfirmRemove(false)}
                            style={{
                                backgroundColor: '#6b7280',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px'
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                ) : (
                    <Button
                        danger
                        onClick={() => setShowConfirmRemove(true)}
                        style={{
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px'
                        }}
                    >
                        Remove Bullhorn Integration
                    </Button>
                )}
            </div>
        );
    }

    return (
        <div>
            <style jsx>{`
                .ant-form-item-label > label {
                    color: #ffffff !important;
                    font-weight: 500 !important;
                }
                .ant-form-item-required::before {
                    color: #ef4444 !important;
                }
                .ant-input,
                .ant-input-password {
                    background-color: rgba(255, 255, 255, 0.9) !important;
                    border: 1px solid rgba(255, 255, 255, 0.3) !important;
                    color: #000000 !important;
                }
                .ant-input:focus,
                .ant-input-password:focus,
                .ant-input-focused,
                .ant-input-password-focused {
                    border-color: #FA370A !important;
                    box-shadow: 0 0 0 2px rgba(250, 55, 10, 0.2) !important;
                }
                .ant-input::placeholder,
                .ant-input-password input::placeholder {
                    color: #666666 !important;
                }
            `}</style>

            <h3 style={{ color: '#ffffff', marginBottom: '24px' }}>Set up Bullhorn Integration</h3>
            <Form
                form={form}
                layout="vertical"
                onFinish={startBullhornAuth}
            >
                <Form.Item
                    name="apiUsername"
                    label={<span style={{ color: '#ffffff', fontSize: '14px' }}>* API Username</span>}
                    rules={[{ required: true, message: 'Please enter your API username' }]}
                >
                    <Input
                        placeholder="Enter your API username"
                        style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: '8px',
                            padding: '8px 12px'
                        }}
                    />
                </Form.Item>

                <Form.Item
                    name="apiPassword"
                    label={<span style={{ color: '#ffffff', fontSize: '14px' }}>* API Password</span>}
                    rules={[{ required: true, message: 'Please enter your API password' }]}
                >
                    <Input.Password
                        placeholder="Enter your API password"
                        style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: '8px'
                        }}
                    />
                </Form.Item>

                <Form.Item
                    name="clientID"
                    label={<span style={{ color: '#ffffff', fontSize: '14px' }}>* Client ID</span>}
                    rules={[{ required: true, message: 'Please enter your client ID' }]}
                >
                    <Input
                        placeholder="Enter your client ID"
                        style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: '8px',
                            padding: '8px 12px'
                        }}
                    />
                </Form.Item>

                <Form.Item
                    name="clientSecret"
                    label={<span style={{ color: '#ffffff', fontSize: '14px' }}>* Client Secret</span>}
                    rules={[{ required: true, message: 'Please enter your client secret' }]}
                >
                    <Input.Password
                        placeholder="Enter your client secret"
                        style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: '8px'
                        }}
                    />
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={isSubmitting}
                        style={{
                            backgroundColor: '#22c55e',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px 24px',
                            height: 'auto',
                            fontWeight: '500'
                        }}
                    >
                        Set up Bullhorn Integration
                    </Button>
                    <p style={{
                        marginTop: 16,
                        fontSize: 13,
                        color: 'rgba(255, 255, 255, 0.7)',
                        lineHeight: '1.4'
                    }}>
                        Note: You'll be redirected to Bullhorn to authenticate.
                        You'll need to have a Bullhorn account with appropriate API permissions.
                    </p>
                </Form.Item>
            </Form>
        </div>
    );
};

export default BullhornIntegration;